import { StarBadge } from "./StarBadge";
import type { RestaurantWithSlots } from "@/lib/store";
import type { Locale } from "@/lib/dictionaries";
import type { MealDiff, RestaurantDiff } from "@/lib/availabilityDiff";

type BookingDict = {
  booking: { sevenrooms: string; zenchef: string };
  stars: { singular: string; plural: string };
  breakfast: string;
  lunch: string;
  dinner: string;
  availabilityDiff: { new: string; newlyAvailable: string; recentlyTaken: string };
};

function MealDiffBadges({ dict, mealDiff }: { dict: BookingDict; mealDiff?: MealDiff }) {
  const hasNew = (mealDiff?.newSlots.size ?? 0) > 0;
  const someTaken = mealDiff?.someTaken ?? false;
  if (!hasNew && !someTaken) return null;
  return (
    <>
      {hasNew && (
        <span className="text-emerald-400 text-[10px] font-medium bg-emerald-500/10 px-1.5 py-0.5 rounded">
          {dict.availabilityDiff.new}
        </span>
      )}
      {someTaken && (
        <span className="text-amber-400 text-[10px] font-medium bg-amber-500/10 px-1.5 py-0.5 rounded">
          {dict.availabilityDiff.recentlyTaken}
        </span>
      )}
    </>
  );
}

function SlotChip({
  slot,
  isNew,
  dict,
}: {
  slot: string;
  isNew: boolean;
  dict: BookingDict;
}) {
  return (
    <span
      title={isNew ? dict.availabilityDiff.newlyAvailable : undefined}
      className={
        isNew
          ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 text-xs font-mono px-2.5 py-1 rounded-md"
          : "bg-neutral-800 text-neutral-200 text-xs font-mono px-2.5 py-1 rounded-md"
      }
    >
      {slot}
    </span>
  );
}

function bookingUrl(restaurant: RestaurantWithSlots): string | null {
  if (restaurant.platform === "sevenrooms") {
    if (!restaurant.sevenroomsSlug) return null;
    return `https://www.sevenrooms.com/reservations/${restaurant.sevenroomsSlug}`;
  }
  if (!restaurant.zenchefId) return null;
  return `https://bookings.zenchef.com/results?rid=${restaurant.zenchefId}`;
}

export function RestaurantCard({
  restaurant,
  lang,
  dict,
  diff,
}: {
  restaurant: RestaurantWithSlots;
  lang: Locale;
  dict: BookingDict;
  diff?: RestaurantDiff;
}) {
  const url = bookingUrl(restaurant);
  const platformLabel =
    restaurant.platform === "sevenrooms"
      ? dict.booking.sevenrooms
      : dict.booking.zenchef;
  const cuisine =
    lang === "en" && restaurant.cuisine_en
      ? restaurant.cuisine_en
      : restaurant.cuisine;
  const starAriaLabel = `${restaurant.stars} ${
    restaurant.stars > 1 ? dict.stars.plural : dict.stars.singular
  }`;

  return (
    <article className="bg-neutral-900 border border-neutral-800 rounded-xl p-5 flex flex-col gap-3 hover:border-neutral-700 transition-colors">
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-col gap-1">
          <h2 className="text-white font-semibold text-base leading-tight">
            {restaurant.website ? (
              <a
                href={restaurant.website}
                target="_blank"
                rel="noopener noreferrer"
                title={restaurant.address}
                className="hover:underline underline-offset-2 decoration-neutral-500"
              >
                {restaurant.name}
              </a>
            ) : (
              <span title={restaurant.address}>{restaurant.name}</span>
            )}
          </h2>
          <p className="text-neutral-500 text-xs">
            {[restaurant.neighborhood, cuisine].filter(Boolean).join(" · ")}
          </p>
        </div>
        <StarBadge count={restaurant.stars} ariaLabel={starAriaLabel} />
      </div>

      {restaurant.breakfastSlots.length > 0 && (
        <div>
          <p className="text-neutral-500 text-xs mb-1 flex items-center gap-1.5 flex-wrap">
            <span>{dict.breakfast}</span>
            <MealDiffBadges dict={dict} mealDiff={diff?.breakfast} />
          </p>
          <div className="flex flex-wrap gap-1.5">
            {restaurant.breakfastSlots.map((slot) => (
              <SlotChip
                key={slot}
                slot={slot}
                isNew={diff?.breakfast.newSlots.has(slot) ?? false}
                dict={dict}
              />
            ))}
          </div>
        </div>
      )}
      {restaurant.lunchSlots.length > 0 && (
        <div>
          <p className="text-neutral-500 text-xs mb-1 flex items-center gap-1.5 flex-wrap">
            <span>{dict.lunch}</span>
            <MealDiffBadges dict={dict} mealDiff={diff?.lunch} />
          </p>
          <div className="flex flex-wrap gap-1.5">
            {restaurant.lunchSlots.map((slot) => (
              <SlotChip
                key={slot}
                slot={slot}
                isNew={diff?.lunch.newSlots.has(slot) ?? false}
                dict={dict}
              />
            ))}
          </div>
        </div>
      )}
      {restaurant.dinnerSlots.length > 0 && (
        <div>
          <p className="text-neutral-500 text-xs mb-1 flex items-center gap-1.5 flex-wrap">
            <span>{dict.dinner}</span>
            <MealDiffBadges dict={dict} mealDiff={diff?.dinner} />
          </p>
          <div className="flex flex-wrap gap-1.5">
            {restaurant.dinnerSlots.map((slot) => (
              <SlotChip
                key={slot}
                slot={slot}
                isNew={diff?.dinner.newSlots.has(slot) ?? false}
                dict={dict}
              />
            ))}
          </div>
        </div>
      )}

      {url && (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-auto text-center text-xs font-medium text-neutral-400 hover:text-white border border-neutral-700 hover:border-neutral-500 rounded-lg py-2 transition-colors"
        >
          {platformLabel}
        </a>
      )}
    </article>
  );
}
